import { normalizeFormElementType } from '@/common/utils/formElementType'
import { isIdentificatorElementType } from '@/common/utils/panelGetDataCollect'
import { setType } from '@/common/utils/typedValueSerializer'

const TEXT_LIVE_FIELD_TYPES = new Set(['TEXT', 'PASSWORD', 'TEXTAREA', 'MULTILINETEXT'])
/** UI-live scalars: text-like + boolean/checkbox + listbox/combobox (boolean code onChange). */
const LIVE_FIELD_TYPES = new Set([
  ...TEXT_LIVE_FIELD_TYPES,
  'BOOLEAN',
  'CHECKBOX',
  'LISTBOX',
  'COMBOBOX',
])

let collectingLiveFieldOverridesDepth = 0

/** True while collectLiveFieldOverrides is reading element.getLiveFieldValue (avoid re-entry). */
export function isCollectingLiveFieldOverrides(): boolean {
  return collectingLiveFieldOverridesDepth > 0
}

export function isTextLikeFormElementType(type: string | undefined | null): boolean {
  const normalized = String(type ?? '').toUpperCase()
  return TEXT_LIVE_FIELD_TYPES.has(normalized)
}

const SELECTION_FIELD_TYPES = new Set(['LISTBOX', 'COMBOBOX', 'MULTISELECT', 'BOOLEAN', 'CHECKBOX'])

export function isSelectionFormElementType(type: string | undefined | null): boolean {
  const normalized = String(type ?? '').toUpperCase()
  return SELECTION_FIELD_TYPES.has(normalized)
}

export function resolveTextInputStoreOptions(
  isTextLike: boolean,
  isCommit: boolean,
  options?: { editableTableUpdate?: boolean; isUserInput?: boolean },
): { skipStore: boolean; skipOnElement: boolean } {
  if (isCommit) {
    return { skipStore: false, skipOnElement: true }
  }
  if (isTextLike) {
    return { skipStore: true, skipOnElement: true }
  }
  const editableTableUpdate = options?.editableTableUpdate ?? false
  const isUserInput = options?.isUserInput ?? false
  const skipStore = !editableTableUpdate && !isUserInput
  return {
    skipStore,
    skipOnElement: editableTableUpdate || skipStore,
  }
}

export function readLiveFieldValueFromElement(element: {
  getLiveFieldValue?: () => unknown
  fieldName?: string
} | null | undefined): unknown {
  if (!element) return undefined
  if (typeof element.getLiveFieldValue === 'function') {
    try {
      return element.getLiveFieldValue()
    } catch {
      /* fall through */
    }
  }
  return undefined
}

type LiveCollectElement = {
  formId?: string | number
  fieldName?: string
  elementType?: string
  elementId?: string | number
  getType?: () => string
  getLiveFieldValue?: () => unknown
  pageDataSlot?: string
}

function flattenLiveCollectElements(
  elements: Record<string, LiveCollectElement>,
  extraMaps?: Array<Record<string, LiveCollectElement> | null | undefined>,
): LiveCollectElement[] {
  const seen = new Set<string>()
  const out: LiveCollectElement[] = []
  const pushAll = (map: Record<string, LiveCollectElement> | null | undefined) => {
    if (!map) return
    for (const [mapKey, element] of Object.entries(map)) {
      if (element == null) continue
      const formId = String(element.formId ?? '').trim()
      const elementId = String(element.elementId ?? '').trim()
      const key = formId || elementId ? `${formId}:${elementId}` : `__anon_${mapKey}`
      if (seen.has(key)) continue
      seen.add(key)
      out.push(element)
    }
  }
  pushAll(elements)
  if (extraMaps) {
    for (const map of extraMaps) pushAll(map)
  }
  return out
}

function sortFillableLiveFirst(elements: LiveCollectElement[]): LiveCollectElement[] {
  return [...elements].sort((a, b) => {
    const aIdent = isIdentificatorElementType(
      normalizeFormElementType(a) ?? a.getType?.() ?? a.elementType,
    )
      ? 1
      : 0
    const bIdent = isIdentificatorElementType(
      normalizeFormElementType(b) ?? b.getType?.() ?? b.elementType,
    )
      ? 1
      : 0
    return aIdent - bIdent
  })
}

/**
 * Примитивные поля с актуальным значением из UI (input/textarea), для merge в reload/action.
 * extraMaps (elementsByKey): fillable TEXT раньше Identificator при одном fieldName.
 */
export function collectLiveFieldOverrides(
  formId: string | number | undefined,
  elements: Record<string, LiveCollectElement>,
  options?: {
    pageDataSlot?: string
    extraMaps?: Array<Record<string, LiveCollectElement> | null | undefined>
  },
): Record<string, { type: string; value: unknown }> {
  const wanted = formId != null && formId !== '' && formId !== -1 ? String(formId) : ''
  const wantedSlot = String(options?.pageDataSlot ?? '').trim()
  if (!wanted && !wantedSlot) return {}
  const out: Record<string, { type: string; value: unknown }> = {}

  collectingLiveFieldOverridesDepth += 1
  try {
    const widgets = sortFillableLiveFirst(
      flattenLiveCollectElements(elements, options?.extraMaps),
    )
    for (const element of widgets) {
      if (element == null) continue
      const formIdMatch = wanted !== '' && String(element.formId ?? '') === wanted
      const slotMatch =
        wantedSlot !== '' && String(element.pageDataSlot ?? '').trim() === wantedSlot
      if (!formIdMatch && !slotMatch) continue
      const fieldName = String(element.fieldName ?? '').trim()
      if (!fieldName) continue
      if (out[fieldName] != null) continue
      const type = normalizeFormElementType(element)?.toUpperCase() ?? element.getType?.()?.toUpperCase() ?? ''
      if (!LIVE_FIELD_TYPES.has(type)) continue

      const live = readLiveFieldValueFromElement(element)
      if (live === undefined) continue
      if (typeof live === 'string' && live.trim() === '') continue

      const typed = setType(live)
      out[fieldName] = typed
    }
  } finally {
    collectingLiveFieldOverridesDepth -= 1
  }

  return out
}

/**
 * For limitToSelectedData (EdiTable cell onChange): keep row-scoped payload, but still
 * carry BOOLEAN/CHECKBOX live clicks that race with cell blur.
 */
export function pickBooleanLiveFieldOverrides(
  overrides: Record<string, { type: string; value: unknown }> | null | undefined,
): Record<string, { type: string; value: unknown }> {
  if (!overrides || typeof overrides !== 'object') return {}
  const out: Record<string, { type: string; value: unknown }> = {}
  for (const [key, field] of Object.entries(overrides)) {
    if (!field || typeof field !== 'object') continue
    const type = String(field.type ?? '').toUpperCase()
    if (type === 'BOOLEAN' || type === 'CHECKBOX') {
      out[key] = field
    }
  }
  return out
}

export function mergeLiveFieldOverridesIntoFlatFormData(
  base: Record<string, unknown>,
  overrides: Record<string, { type: string; value: unknown }>,
): Record<string, unknown> {
  if (!overrides || Object.keys(overrides).length === 0) return base
  const merged = { ...base }
  for (const [key, field] of Object.entries(overrides)) {
    merged[key] = field.value
  }
  return merged
}

export function mergeLiveFieldOverridesIntoTypedFormData(
  base: Record<string, unknown>,
  overrides: Record<string, { type: string; value: unknown }>,
): Record<string, unknown> {
  if (!overrides || Object.keys(overrides).length === 0) return base
  return { ...base, ...overrides }
}
